import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import Dropzone from "@/components/Dropzone";
import { parseBankCSV } from "@/lib/csv";
import { MagicWand, FileCsv, Bank as BankIcon, FilePdf, ArrowsClockwise, LinkSimple, LinkBreak, Copy, Trash, CheckCircle, X, DownloadSimple, Receipt as ReceiptIcon } from "@phosphor-icons/react";

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(",")[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function Bank() {
  const [txns, setTxns] = useState([]);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ocring, setOcring] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [dupes, setDupes] = useState(null);
  const [dupeSel, setDupeSel] = useState({});
  const [matchFor, setMatchFor] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [vat, setVat] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/bank/transactions"); setTxns(r.data);
    try { const v = await api.get("/bank/vat-reclaim"); setVat(v.data); } catch { /* optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const auditExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/bank/audit-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `percen360-audit-${new Date().toISOString().slice(0,10)}.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { notifyError(e); } finally { setExporting(false); }
  };

  const isDupPreview = (r) => txns.some((t) =>
    Number(t.amount).toFixed(2) === Number(r.amount).toFixed(2) &&
    (t.date || "").slice(0,10) === (r.date || "").slice(0,10) &&
    (t.description || "").toLowerCase().trim() === (r.description || "").toLowerCase().trim());

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/bank/reconcile");
      alert(r.data.matched > 0 ? `Matched ${r.data.matched} transaction${r.data.matched === 1 ? "" : "s"} to receipts.` : "No new matches — add receipts with matching amounts/dates, then reconcile again.");
      load();
    } catch (e) { notifyError(e); } finally { setReconciling(false); }
  };

  const unmatch = async (txn_id) => {
    try { await api.post("/bank/unmatch", { txn_id }); load(); } catch (e) { notifyError(e); }
  };

  const deleteTxn = async (txn_id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/bank/transactions/${txn_id}`); load(); } catch (e) { notifyError(e); }
  };

  const openMatch = async (txn) => {
    setMatchFor(txn); setCandidates([]);
    try { const r = await api.get(`/bank/match-candidates/${txn.txn_id}`); setCandidates(r.data.candidates || []); } catch (e) { notifyError(e); }
  };

  const doMatch = async (c) => {
    try {
      await api.post("/bank/match", { txn_id: matchFor.txn_id, source: c.source, source_id: c.id });
      setMatchFor(null); load();
    } catch (e) { notifyError(e); }
  };

  const findDupes = async () => {
    try {
      const r = await api.get("/bank/duplicates");
      setDupes(r.data.groups || []); setDupeSel({});
    } catch (e) { notifyError(e); }
  };

  const openMatchedFile = async (t) => {
    const url = t.matched_type === "document" ? `/documents/${t.matched_id}/file` : `/receipts/${t.matched_id}/file`;
    try {
      const r = await api.get(url, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch (e) { notifyError(e, "No original file stored"); }
  };

  const deleteSelectedDupes = async () => {
    const ids = Object.keys(dupeSel).filter((k) => dupeSel[k]);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} duplicate transaction${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      for (const id of ids) await api.delete(`/bank/transactions/${id}`);
      setDupes(null); load();
    } catch (e) { notifyError(e); }
  };

  const onFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseBankCSV(text);
      setPreview(parsed);
    } catch (e) {
      alert(e.message || "Could not parse CSV");
    }
  };

  const onStatementFile = async (file) => {
    if (!file) return;
    setOcring(true);
    try {
      const b64 = await fileToB64(file);
      const r = await api.post("/bank/ocr", { file_base64: b64 });
      const rows = (r.data.transactions || []).map((t) => ({
        date: t.date,
        description: t.description,
        amount: Number(t.amount || 0),
        category: t.category || "Uncategorized",
      }));
      if (!rows.length) {
        alert("AI couldn't read any transactions from that file. Try a clearer image or PDF.");
      } else {
        setPreview(rows);
      }
    } catch (e) {
      alert("Bank OCR failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setOcring(false);
    }
  };

  const confirmImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      await api.post("/bank/import", { rows: preview });
      setPreview(null);
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setImporting(false); }
  };

  const categorize = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bank/categorize");
      alert(`Auto-categorised ${r.data.updated} transactions`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Bank & Wallet</div>
          <h1 className="font-display text-4xl">Import & auto-categorise</h1>
          <p className="text-white/60 text-sm mt-2">Drop a bank CSV — or an image/PDF of your statement and AI reads the transactions. Claude then auto-categorises expenses.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="btn-reconcile" onClick={reconcile} disabled={reconciling || txns.length === 0} className="btn-ghost">
            <ArrowsClockwise size={16} weight="fill" /> {reconciling ? "Matching…" : "Reconcile receipts"}
          </button>
          <button data-testid="btn-find-dupes" onClick={findDupes} disabled={txns.length === 0} className="btn-ghost">
            <Copy size={16} weight="fill" /> Find duplicates
          </button>
          <button data-testid="btn-audit-export" onClick={auditExport} disabled={exporting || txns.length === 0} className="btn-ghost">
            <DownloadSimple size={16} weight="fill" /> {exporting ? "Building…" : "Audit export"}
          </button>
          <button data-testid="btn-categorize" onClick={categorize} disabled={busy || txns.length === 0} className="btn-gold">
            <MagicWand size={16} weight="fill" /> {busy ? "Analysing…" : "AI categorise"}
          </button>
        </div>
      </div>

      {/* Dropzones */}
      {!preview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropzone
            testId="dropzone-csv"
            accept=".csv,text/csv"
            icon={BankIcon}
            label="Drop bank CSV here"
            sublabel="AIB · BoI · Revolut · Wise · N26 — we auto-detect columns"
            onFile={onFile}
          />
          <Dropzone
            testId="dropzone-statement"
            accept="image/*,application/pdf,.pdf,.heic,.heif"
            icon={FilePdf}
            label={ocring ? "AI reading your statement…" : "Drop statement image or PDF"}
            sublabel="Photo, screenshot or PDF (multi-page) — AI extracts every transaction line"
            onFile={onStatementFile}
          />
        </div>
      )}

      {preview && (
        <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Preview</div>
              <div className="font-display text-xl mt-1">{preview.length} transactions detected</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPreview(null)} className="btn-ghost">Discard</button>
              <button data-testid="btn-confirm-import" onClick={confirmImport} disabled={importing} className="btn-gold">
                <FileCsv size={16} weight="fill" /> {importing ? "Importing…" : `Import ${preview.length} rows`}
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="data">
              <thead><tr><th>Date</th><th>Description</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {preview.slice(0, 15).map((r, i) => (
                  <tr key={`${r.date}-${r.description}-${r.amount}-${i}`}><td className="mono text-xs">{r.date}</td><td>{r.description} {isDupPreview(r) && <span data-testid="preview-dupe-badge" className="ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[#FFB020]/20 text-[#FFB020]">possible duplicate</span>}</td><td className={`num ${r.amount<0?"text-[#E63946]":"text-[#00A86B]"}`}>€{r.amount.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
            {preview.length > 15 && <div className="text-xs text-white/40 text-center py-2">…and {preview.length - 15} more</div>}
          </div>
        </motion.div>
      )}

      {vat && (vat.reclaimable_count > 0 || vat.unevidenced_count > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="vat-reclaim-card">
          <div className="glass rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/50 flex items-center gap-1.5"><ReceiptIcon size={13} weight="fill" className="text-[#00A86B]" /> Reclaimable VAT</div>
            <div className="font-display text-2xl text-[#00A86B] mt-1" data-testid="vat-reclaimable">€{vat.reclaimable_vat.toFixed(2)}</div>
            <div className="text-[11px] text-white/45 mt-0.5">{vat.reclaimable_count} matched receipt{vat.reclaimable_count===1?"":"s"} — evidenced by bank spend</div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Not yet evidenced</div>
            <div className="font-display text-2xl text-[#FFB020] mt-1">€{vat.unevidenced_vat.toFixed(2)}</div>
            <div className="text-[11px] text-white/45 mt-0.5">{vat.unevidenced_count} receipt{vat.unevidenced_count===1?"":"s"} not matched to a transaction yet</div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Total receipt VAT</div>
            <div className="font-display text-2xl mt-1">€{vat.total_vat.toFixed(2)}</div>
            <div className="text-[11px] text-white/45 mt-0.5">Reconcile receipts to move VAT into Reclaimable</div>
          </div>
        </div>
      )}

      <div className="glass rounded-xl overflow-hidden">
        {txns.length === 0 ? (
          <div className="p-12 text-center text-white/50 text-sm">
            No transactions yet. Upload a bank CSV to begin.
          </div>
        ) : (
          <table className="data">
            <thead><tr><th>Date</th><th>Description</th><th>Category</th><th className="text-right">Amount</th><th>Receipt</th><th></th></tr></thead>
            <tbody>
              {txns.map(t => {
                const isExpense = Number(t.amount) < 0;
                return (
                <tr key={t.txn_id} data-testid={`row-txn-${t.txn_id}`}>
                  <td className="mono text-xs text-white/60">{t.date}</td>
                  <td>{t.description}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${t.category==="Uncategorized"?"bg-white/5 text-white/50 border border-white/10":"badge-pass"}`}>{t.category}</span>
                  </td>
                  <td className={`num ${t.amount<0?"text-[#E63946]":"text-[#00A86B]"}`}>€{Number(t.amount).toFixed(2)}</td>
                  <td data-testid={`recon-${t.txn_id}`}>
                    {t.matched ? (
                      <button onClick={() => openMatchedFile(t)} data-testid={`btn-view-matched-${t.txn_id}`} className="inline-flex items-center gap-1.5 text-[#00A86B] text-xs hover:underline" title={`Open ${t.matched_label} (audit)`}>
                        <span className="w-2 h-2 rounded-full bg-[#00A86B]" /> <CheckCircle size={13} weight="fill" />
                        <span className="max-w-[120px] truncate">{t.matched_label}</span>
                      </button>
                    ) : isExpense ? (
                      <span className="inline-flex items-center gap-1.5 text-[#E63946] text-xs">
                        <span className="w-2 h-2 rounded-full bg-[#E63946]" /> Unmatched
                      </span>
                    ) : <span className="text-white/25 text-xs">—</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {t.matched ? (
                      <button data-testid={`btn-unmatch-${t.txn_id}`} onClick={() => unmatch(t.txn_id)} title="Unmatch" className="text-white/40 hover:text-white mr-2"><LinkBreak size={16} /></button>
                    ) : isExpense ? (
                      <button data-testid={`btn-match-${t.txn_id}`} onClick={() => openMatch(t)} title="Match to a receipt" className="text-white/40 hover:text-[#FFB020] mr-2"><LinkSimple size={16} /></button>
                    ) : null}
                    <button data-testid={`btn-del-txn-${t.txn_id}`} onClick={() => deleteTxn(t.txn_id)} title="Delete" className="text-white/40 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {/* Manual match modal */}
      <AnimatePresence>
        {matchFor && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="match-modal">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}} className="glass rounded-2xl w-full max-w-lg my-8 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="eyebrow mb-1">Match a receipt</div>
                  <div className="font-display text-xl">€{Math.abs(Number(matchFor.amount)).toFixed(2)} · {matchFor.description}</div>
                </div>
                <button onClick={() => setMatchFor(null)} data-testid="btn-close-match" className="text-white/60 hover:text-white"><X size={20}/></button>
              </div>
              {candidates.length === 0 ? (
                <div className="text-white/50 text-sm py-8 text-center">No unmatched receipts or documents found. Add a receipt with this amount, then try again.</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-auto">
                  {candidates.map((c) => (
                    <button key={`${c.source}-${c.id}`} data-testid={`match-cand-${c.id}`} onClick={() => doMatch(c)}
                      className="w-full flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 hover:border-[#FFB020] px-3 py-2 text-left">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{c.label}</div>
                        <div className="text-[11px] text-white/45">{c.source} · {c.date || "no date"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="num text-sm">€{Number(c.amount).toFixed(2)}</div>
                        {c.amount_gap > 0 && <div className="text-[10px] text-[#FFB020]">±€{c.amount_gap.toFixed(2)}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Duplicates review modal */}
      <AnimatePresence>
        {dupes && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="dupes-modal">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}} className="glass rounded-2xl w-full max-w-2xl my-8 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="eyebrow mb-1">Duplicate transactions</div>
                  <div className="font-display text-xl">{dupes.length === 0 ? "None found" : `${dupes.length} group${dupes.length===1?"":"s"} of duplicates`}</div>
                </div>
                <button onClick={() => setDupes(null)} data-testid="btn-close-dupes" className="text-white/60 hover:text-white"><X size={20}/></button>
              </div>
              {dupes.length === 0 ? (
                <div className="text-white/50 text-sm py-8 text-center">No duplicate transactions detected. 🎉</div>
              ) : (
                <>
                  <p className="text-white/55 text-xs mb-3">We keep the first of each group by default. Tick the ones you want to remove, then confirm.</p>
                  <div className="space-y-4 max-h-96 overflow-auto">
                    {dupes.map((g, gi) => (
                      <div key={`${g.date}-${g.amount}-${gi}`} className="rounded-lg border border-white/10 p-3">
                        <div className="text-xs text-white/50 mb-2">€{Number(g.amount).toFixed(2)} · {g.date}</div>
                        {g.items.map((it, idx) => (
                          <label key={it.txn_id} className="flex items-center gap-3 py-1.5 cursor-pointer">
                            <input type="checkbox" data-testid={`dupe-check-${it.txn_id}`} className="w-auto"
                              checked={!!dupeSel[it.txn_id]} disabled={idx === 0}
                              onChange={(e) => setDupeSel((s) => ({ ...s, [it.txn_id]: e.target.checked }))} />
                            <span className="text-sm flex-1 truncate">{it.description}</span>
                            {idx === 0 && <span className="text-[10px] text-[#00A86B] uppercase tracking-wider">keep</span>}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-white/5 mt-4">
                    <button onClick={() => setDupes(null)} className="btn-ghost">Cancel</button>
                    <button data-testid="btn-delete-dupes" onClick={deleteSelectedDupes} className="btn-gold">
                      <Trash size={15} weight="fill" /> Delete selected
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
